import { Stack, Typography } from "@mui/material"
import { Avatar, Button, Chip, Dropdown, getCalendarFormat } from "@cakemail-org/ui-components-v2"
import { TUserRowProps } from "./types"
import { NameTableCol } from "../../../../components/tables/nameTableCol"

export function UserRow({
    user,
    index,
    loading,
    isOwner,
    isCurrentUser,
    isExpired,
    expirationMessage,
    rowDropdownOptions,
    handleViewUser,
    handleResendVerificationEmail,
    t
}: TUserRowProps) {
    const actions = (
        <Stack direction="row" gap={3} alignItems="center" justifyContent="flex-end" minWidth="100%">
            {user.status === "pending" && !isExpired && (
                <>
                    <Typography variant="body2" color="text.secondary">
                        {expirationMessage}
                    </Typography>
                    <Button
                        onClick={() => handleResendVerificationEmail(user)}
                        size="small"
                        color="secondary"
                        variant="text"
                        disabled={loading}
                    >
                        {t("user-resend-verification")}
                    </Button>
                </>
            )}
            <Button
                onClick={() => handleViewUser(user)}
                size="small"
                color="body1"
                variant="outlined"
                disabled={loading}
            >
                {t("general-view_one")}
            </Button>
            {rowDropdownOptions.length > 0 && (
                <Dropdown
                    options={rowDropdownOptions.map(option => ({
                        ...option,
                        disabled: option.value === "delete-user" && (isCurrentUser || isOwner),
                        onClick: option.onClick ? (() => (option.onClick as any)?.(user)) : undefined
                    }))}
                    placeholder={""}
                    size="medium"
                    color="body1"
                    variant="outlined"
                    iconOnly
                />
            )}
        </Stack>
    )

    const userCell = (
        <NameTableCol
            avatar={
                <Avatar
                    email={user.email || ""}
                    firstName={user.first_name || ""}
                    lastName={user.last_name || ""}
                />
            }
            name={`${user.first_name || ""} ${user.last_name || ""}`.trim()}
        />
    )

    let statusChip
    if (user.status === "pending") {
        if (isExpired) {
            statusChip = (
                <Chip
                    label={t("user-status-expired")}
                    color="cloud"
                    variant="outlined"
                    size="small"
                />
            )
        } else {
            statusChip = (
                <Chip
                    label={t("user-status-invited")}
                    color="cloud"
                    variant="outlined"
                    size="small"
                />
            )
        }
    } else if (user.status === "active") {
        statusChip = (
            <Chip
                label={t("user-status-active")}
                color="secondary"
                variant="outlined"
                size="small"
            />
        )
    }

    const ownerChip = isOwner ? (
        <Chip
            label={t("user-status-owner")}
            color="primary"
            variant="filled"
            size="small"
        />
    ) : null

    return {
        id: index,
        name: { id: `${user.first_name || ""} ${user.last_name || ""}`.trim(), content: userCell },
        email: user.email || "",
        last_activity_on: user.last_activity_on ? getCalendarFormat({ unixDate: user.last_activity_on }) : "",
        created_on: user.created_on ? getCalendarFormat({ unixDate: user.created_on }) : "",
        status: statusChip,
        owner: ownerChip,
        actions: actions
    }
}
